/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobeIcon, ShuffleIcon, MixerHorizontalIcon, DashboardIcon, SizeIcon } from '@radix-ui/react-icons';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import { CityAutocompleteInput } from '@/shared/ui/components/form/CityAutocompleteInput';
import { Select, TextInput } from '@/shared/ui';
import type { PropertyFilters } from './constants';
import { translatePropertyChipLabel } from '@/shared/utils/property-chip-label';
import { maskArea, maskMoney } from './masks';
import { BRAZILIAN_STATES, PROPERTY_CATEGORIES, TRANSACTION_TYPES } from './constants';
import { SectionHeader, FieldLabel, ChipBtn, ToggleBtn, QtyBtn } from './parts';
import { FilterSectionCondo } from './FilterSectionCondo';
import { FilterSectionAttributes } from './FilterSectionAttributes';
import { FilterSectionBuilding } from './FilterSectionBuilding';
import { FilterSectionAmenities } from './FilterSectionAmenities';
import { FilterSectionCondoAmenities } from './FilterSectionCondoAmenities';
import { FilterSectionRental } from './FilterSectionRental';
import { FilterSectionParking } from './FilterSectionParking';

interface BodyProps {
  filters: PropertyFilters;
  setFilters: (f: PropertyFilters) => void;
  toggleInArray: (field: keyof PropertyFilters, value: string) => void;
}

/**
 * Returns whether a value is present in the current filter set.
 */
const has = (filters: PropertyFilters, field: keyof PropertyFilters, value: string) =>
  ((filters[field] as string[] | undefined) || []).includes(value);

const PriceField: React.FC<{ label: string; placeholder: string; field: keyof PropertyFilters; format?: (raw: string) => string | undefined } & BodyProps> = ({ label, placeholder, field, format, filters, setFilters }) => (
  <TextInput label={label} placeholder={placeholder} value={(filters[field] as string) || ''} onChange={(e) => setFilters({ ...filters, [field]: format ? format(e.target.value) : e.target.value })} />
);

const QtyRow: React.FC<{ label: string; field: keyof PropertyFilters; prefix: string } & BodyProps> = ({ label, field, prefix, filters, setFilters }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
      {['1','2','3','4','5+'].map((n) => <QtyBtn key={`${prefix}-${n}`} value={n} active={filters[field] === n} onClick={() => setFilters({ ...filters, [field]: filters[field] === n ? undefined : n })} />)}
    </div>
  </div>
);

/** Main filter panel body — renders all filter sections in a scrollable column. */
export const FilterBody: React.FC<BodyProps> = (props) => {
  const { t } = useTranslation('features/properties');
  const { filters, setFilters, toggleInArray } = props;
  return (
    <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <SectionHeader icon={<GlobeIcon width={14} height={14} />} title={t('filterPanel.location')} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div>
            <FieldLabel>{t('filterPanel.state')}</FieldLabel>
            <Select data={BRAZILIAN_STATES.map((s) => ({ value: s, label: s }))} value={filters.state || null} onChange={(v: string | string[] | null) => setFilters({ ...filters, state: Array.isArray(v) ? v[0] : (v ?? '') })} placeholder={t('filterPanel.selectState')} />
          </div>
          <div>
            <FieldLabel>{t('filterPanel.city')}</FieldLabel>
            <CityAutocompleteInput placeholder={t('filterPanel.searchCity')} value={filters.city ? [filters.city] : []} onChange={(cities) => setFilters({ ...filters, city: cities.length > 0 ? cities[cities.length - 1] : '' })} />
          </div>
          <div>
            <FieldLabel>{t('filterPanel.neighborhood')}</FieldLabel>
            <TextInput placeholder={t('filterPanel.searchNeighborhood')} value={filters.neighborhood || ''} onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })} />
          </div>
        </div>
      </div>

      <div>
        <SectionHeader icon={<BuildingIcon width={14} height={14} />} title={t('filterPanel.propertyType')} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {PROPERTY_CATEGORIES.map((c) => (
            <ChipBtn
              key={c}
              value={translatePropertyChipLabel(t, c)}
              active={has(filters, 'categories', c)}
              onClick={() => toggleInArray('categories', c)}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionHeader icon={<ShuffleIcon width={14} height={14} />} title={t('filterPanel.business')} />
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {TRANSACTION_TYPES.map((tt) => (
            <ToggleBtn
              key={tt}
              value={translatePropertyChipLabel(t, tt)}
              active={has(filters, 'transactions', tt)}
              onClick={() => toggleInArray('transactions', tt)}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionHeader icon={<MixerHorizontalIcon width={14} height={14} />} title={t('filterPanel.priceRange')} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <PriceField {...props} format={maskMoney} label={t('filterPanel.minSale')} placeholder="R$ 0" field="priceMinSale" />
          <PriceField {...props} format={maskMoney} label={t('filterPanel.maxSale')} placeholder="R$ 5.000.000" field="priceMaxSale" />
          <PriceField {...props} format={maskMoney} label={t('filterPanel.minRent')} placeholder="R$ 0" field="priceMinRent" />
          <PriceField {...props} format={maskMoney} label={t('filterPanel.maxRent')} placeholder="R$ 10.000" field="priceMaxRent" />
        </div>
      </div>

      <div>
        <SectionHeader icon={<DashboardIcon width={14} height={14} />} title={t('filterPanel.characteristics')} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <QtyRow {...props} label={t('filterPanel.bedrooms')} field="bedrooms" prefix="bed" />
          <QtyRow {...props} label={t('filterPanel.bathrooms')} field="bathrooms" prefix="bath" />
          <QtyRow {...props} label={t('filterPanel.parking')} field="parking" prefix="park" />
          <QtyRow {...props} label={t('filterPanel.suites')} field="suites" prefix="suite" />
        </div>
      </div>

      <div>
        <SectionHeader icon={<SizeIcon width={14} height={14} />} title={t('filterPanel.area')} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <PriceField {...props} format={maskArea} label={t('filterPanel.builtMin')} placeholder="0 m²" field="areaBuiltMin" />
          <PriceField {...props} format={maskArea} label={t('filterPanel.builtMax')} placeholder="500 m²" field="areaBuiltMax" />
          <PriceField {...props} format={maskArea} label={t('filterPanel.totalMin')} placeholder="0 m²" field="areaTotalMin" />
          <PriceField {...props} format={maskArea} label={t('filterPanel.totalMax')} placeholder="1000 m²" field="areaTotalMax" />
        </div>
      </div>

      <FilterSectionCondo filters={filters} setFilters={setFilters} />
      <FilterSectionAttributes filters={filters} setFilters={setFilters} />
      <FilterSectionBuilding filters={filters} setFilters={setFilters} />
      <FilterSectionAmenities filters={filters} toggleInArray={toggleInArray} />
      <FilterSectionCondoAmenities filters={filters} toggleInArray={toggleInArray} />
      {(filters.transactions || []).includes('Aluguel') && (
        <FilterSectionRental filters={filters} setFilters={setFilters} toggleInArray={toggleInArray} />
      )}
      <FilterSectionParking filters={filters} toggleInArray={toggleInArray} />
    </div>
  );
};
